/**
 * Google Analytics 4 (gtag.js) setup.
 * Measurement ID comes from the linked Google Analytics connector.
 */

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

const measurementId = import.meta.env
  .VITE_LOVABLE_CONNECTOR_GOOGLE_ANALYTICS_API_KEY as string | undefined;

let initialized = false;

// gtag.js only processes dataLayer entries pushed as an `arguments` object,
// so this must stay a normal function that forwards `arguments` verbatim.
export function gtag(...args: unknown[]) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export const initAnalytics = () => {
  if (initialized || typeof window === "undefined") return;
  if (!measurementId) {
    console.warn("[analytics] No GA4 measurement ID configured.");
    return;
  }
  initialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  gtag("js", new Date());
  gtag("config", measurementId);

  attachEngagementTracking();
};

export const trackPageView = (path: string, title?: string) => {
  if (!measurementId) return;
  gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
};

export const trackEvent = (
  name: string,
  params: Record<string, unknown> = {}
) => {
  if (!measurementId) return;
  gtag("event", name, params);
};

/**
 * Passive engagement signals: outbound link clicks, mailto clicks and
 * scroll depth milestones. These give visitor-behaviour detail beyond
 * raw page views.
 */
let engagementAttached = false;
const attachEngagementTracking = () => {
  if (engagementAttached) return;
  engagementAttached = true;

  document.addEventListener(
    "click",
    (event) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href") ?? "";
      if (!href) return;

      if (href.startsWith("mailto:")) {
        trackEvent("email_click", { link_url: href });
        return;
      }
      if (/^https?:/i.test(href) && !href.includes(window.location.host)) {
        trackEvent("outbound_click", {
          link_url: href,
          link_text: anchor.textContent?.trim().slice(0, 100),
        });
      }
    },
    { capture: true }
  );

  const milestones = [25, 50, 75, 90];
  let reached = new Set<number>();
  const onScroll = () => {
    const doc = document.documentElement;
    const height = doc.scrollHeight - window.innerHeight;
    if (height <= 0) return;
    const percent = Math.round((window.scrollY / height) * 100);
    for (const m of milestones) {
      if (percent >= m && !reached.has(m)) {
        reached.add(m);
        trackEvent("scroll_depth", {
          percent_scrolled: m,
          page_path: window.location.pathname,
        });
      }
    }
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("popstate", () => {
    reached = new Set<number>();
  });
};

export const resetScrollDepth = () => {
  // Route changes restart the scroll-depth funnel.
  window.dispatchEvent(new PopStateEvent("popstate"));
};
